import { useContext, useState, useEffect, useRef } from 'react';
import { ERROR_TEXT } from '../../../../constants';
import { useAxiosAuthenticated } from '../../../../useAxiosAuthenticated';
import { ReportContext } from '../../../../context/ReportContext';
import ReportCardHeader from './ReportCardHeader';
import UserMatchesTable from './UserMatchesTable';
import UserDetailsSection from './UserDetailsSection';
import ErrorCard from '../../../Cards/ErrorCard';

const UserReportCard = () => {
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [primaryEmail, setPrimaryEmail] = useState('');
    const [userId, setUserId] = useState('');
    const [localizedCountry, setLocalizedCountry] = useState('');
    const [localizedLanguage, setLocalizedLanguage] = useState('');
    const [unitId, setUnitId] = useState('');
    const [cobrandId, setCobrandId] = useState('');
    const [inFlight, setInFlight] = useState(false);
    const [userReportInFlight, setUserReportInFlight] = useState(false);
    const [locationLanguageData, setLocationLanguageData] = useState({});
    const [reportDataCollection, setReportDataCollection] = useState(null);
    const [isRequestError, setIsRequestError] = useState(false);
    const [userMatches, setUserMatches] = useState([]);
    const [hasMadeFirstRequest, setHasMadeFirstRequest] = useState(false);
    const [unitsError, setUnitsError] = useState('');
    const allUnitsObject = { id: 0, name: 'All Units' };
    const [unitOptionsForSelectedCobrand, setUnitOptionsForSelectedCobrand] = useState([
        allUnitsObject,
    ]);

    const { get, post, tokenError } = useAxiosAuthenticated();
    const { cobrandOptions, unitOptions } = useContext(ReportContext);
    const reportSectionRef = useRef(null);

    useEffect(() => {
        setUnitId('');
        const loadUnits = async () => {
            // If no cobrand selected, use units from ReportContext
            if (!cobrandId) {
                setUnitsError('');
                const opts = Array.isArray(unitOptions) ? unitOptions : [allUnitsObject];
                setUnitOptionsForSelectedCobrand(opts);
                return;
            }
            try {
                setUnitsError('');
                const url = `${process.env.REACT_APP_API_BASE_URL}/reports-meta/units?cobrandId=${cobrandId}`;
                const resp = await get(url);
                const options = Array.isArray(resp?.data)
                    ? resp.data.map((x) => ({ id: x.unitId, name: x.name }))
                    : [];
                setUnitOptionsForSelectedCobrand([allUnitsObject, ...options]);
            } catch (e) {
                console.error('Error fetching units:', e);
                setUnitsError(ERROR_TEXT.cobrands);
                setUnitOptionsForSelectedCobrand([allUnitsObject]);
            }
        };
        loadUnits();
    }, [cobrandId, unitOptions]);

    useEffect(() => {
        setInFlight(true);
        const getCobrandLocationLanguage = async () => {
            if (!cobrandOptions?.[0]?.id) return;
            const url = `${process.env.REACT_APP_API_BASE_URL}/reports-meta/cobrand-location-language?cobrandId=${cobrandOptions[0].id}`;
            try {
                const resp = await get(url);
                setLocationLanguageData(resp.data);
                setInFlight(false);
            } catch (error) {
                console.error('Error fetching location/language data:', error);
                setInFlight(false);
            }
        };
        getCobrandLocationLanguage();
    }, [cobrandOptions]);

    const handleSelectUser = async (user) => {
        setUserReportInFlight(true);
        setIsRequestError(false);

        try {
            const userId = user?.userId || user?.UserId;
            if (!userId) {
                console.error('No userId found for user:', user);
                setIsRequestError(true);
                return;
            }

            const toSend = {
                CobrandIds: cobrandId ? [cobrandId] : cobrandOptions.map((x) => x.id),
                UserId: userId,
            };

            const url = `${process.env.REACT_APP_API_BASE_URL}/reports/user-report-card/single-user-report-card`;
            const resp = await post(url, toSend);
            const data = resp?.data;

            if (data) {
                setReportDataCollection(data);
                // collapse results by default when a single user's report is loaded
            } else {
                setIsRequestError(true);
            }
        } catch (error) {
            console.error('Error fetching single user report:', error);
            setIsRequestError(true);
        } finally {
            setUserReportInFlight(false);
        }
    };

    const handleRun = async () => {
        setIsRequestError(false);

        try {
            setHasMadeFirstRequest(true);
            setInFlight(true);
            setReportDataCollection(null);
            setUserMatches([]);
            // reset collapses on new run

            const parseIntOrUndefined = (val) => {
                const n = parseInt(val, 10);
                return Number.isNaN(n) ? undefined : n;
            };

            const toSend = {
                CobrandIds: cobrandId ? [cobrandId] : cobrandOptions.map((x) => x.id),
                ...(username && { Username: username }),
                ...(firstName && { FirstName: firstName }),
                ...(lastName && { LastName: lastName }),
                ...(primaryEmail && { PrimaryEmailAddress: primaryEmail }),
                ...(parseIntOrUndefined(userId) !== undefined && {
                    UserId: parseIntOrUndefined(userId),
                }),
                ...(localizedCountry && {
                    LocalizedCountry:
                        localizedCountry === '__NULL__' ? null : localizedCountry,
                }),
                ...(localizedLanguage && {
                    LocalizedLanguage:
                        localizedLanguage === '__NULL__' ? null : localizedLanguage,
                }),
                ...(parseIntOrUndefined(unitId) !== undefined && {
                    UnitId: parseIntOrUndefined(unitId),
                }),
            };

            const url = `${process.env.REACT_APP_API_BASE_URL}/reports/user-report-card/user-details`;
            const resp = await post(url, toSend);
            const data = resp?.data;

            if (Array.isArray(data)) {
                setUserMatches(data);
                // If only one user is found, automatically fetch their report
                if (data.length === 1) {
                    await handleSelectUser(data[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            setIsRequestError(true);
        } finally {
            setInFlight(false);
        }
    };

    useEffect(() => {
        if (reportDataCollection && !userReportInFlight) {
            reportSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [reportDataCollection, userReportInFlight]);

    if (tokenError) return <ErrorCard>{ERROR_TEXT.token}</ErrorCard>;

    return (
        <div>
            <ReportCardHeader
                username={username}
                setUsername={setUsername}
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                primaryEmail={primaryEmail}
                setPrimaryEmail={setPrimaryEmail}
                userId={userId}
                setUserId={setUserId}
                localizedCountry={localizedCountry}
                setLocalizedCountry={setLocalizedCountry}
                localizedLanguage={localizedLanguage}
                setLocalizedLanguage={setLocalizedLanguage}
                unitId={unitId}
                setUnitId={setUnitId}
                hasAnyInput={[
                    username,
                    firstName,
                    lastName,
                    primaryEmail,
                    userId,
                    localizedCountry,
                    localizedLanguage,
                    unitId,
                    cobrandId,
                ].some((v) => v && String(v).trim() !== '')}
                handleRun={handleRun}
                inFlight={inFlight}
                locationLanguageData={locationLanguageData}
                unitOptions={unitOptionsForSelectedCobrand}
                cobrandOptions={cobrandOptions}
                cobrandId={cobrandId}
                setCobrandId={setCobrandId}
            />

            {unitsError && <ErrorCard>{unitsError}</ErrorCard>}
            {isRequestError && <ErrorCard>{ERROR_TEXT.reportRequest}</ErrorCard>}

            {hasMadeFirstRequest && !inFlight && (
                <UserMatchesTable
                    userMatches={userMatches}
                    onSelectUser={handleSelectUser}
                    inFlight={userReportInFlight}
                />
            )}

            {userMatches.length > 0 && reportDataCollection && !isRequestError && (
                <div className="my-6 border-t border-gray-300" />
            )}

            {reportDataCollection && !isRequestError && (
                <div ref={reportSectionRef}>
                    <UserDetailsSection
                        reportDataCollection={reportDataCollection}
                        inFlight={userReportInFlight}
                    />
                </div>
            )}
        </div>
    );
};

export default UserReportCard;
